import { useEffect, useRef, useState } from 'react';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { rotaPainel } from './painel';
import {Cartao} from '../componentes/layout';
import { Gravador, type EstadoDoGravador } from '../audio/gravador';
import { duracaoLegivel } from './audio';
import { Tela } from '../workspace/Tela';

const INICIAL: EstadoDoGravador = {
  gravando: false,
  duracaoMs: 0,
  nivel: 0,
  pendentes: 0,
  erro: '',
};

function Gravar(): React.JSX.Element {
  const navegar = useNavigate();
  const [titulo, setTitulo] = useState('');
  const [estado, setEstado] = useState<EstadoDoGravador>(INICIAL);
  const [gravacaoId, setGravacaoId] = useState('');
  const [erroInicio, setErroInicio] = useState('');
  const gravador = useRef<Gravador | null>(null);

  useEffect(() => {
    return () => {
      // Sair da tela no meio da gravação não pode deixar o microfone aberto.
      void gravador.current?.parar();
    };
  }, []);

  async function comecar(): Promise<void> {
    setErroInicio('');
    try {
      const instancia = new Gravador(setEstado);
      gravador.current = instancia;
      const id = await instancia.iniciar(titulo.trim() || 'Gravação sem título');
      setGravacaoId(id);
    } catch (erro) {
      gravador.current = null;
      setErroInicio(
        (erro as Error).name === 'NotAllowedError'
          ? 'O navegador não liberou o microfone. Autorize e tente de novo.'
          : 'Não foi possível começar a gravar neste dispositivo.',
      );
    }
  }

  async function encerrar(): Promise<void> {
    const instancia = gravador.current;
    if (!instancia) return;
    await instancia.parar();
    gravador.current = null;
    if (gravacaoId) void navegar({ to: '/audio/$id', params: { id: gravacaoId } });
  }

  const nivel = Math.min(100, Math.round(estado.nivel * 900));

  return (
    <Tela>
      <Cartao titulo="Gravar">
        {!estado.gravando && !gravacaoId ? (
          <div className="space-y-3">
            <input
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Do que é esta gravação?"
              className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none focus:border-zinc-500"
            />
            <button
              onClick={() => void comecar()}
              className="w-full rounded-md bg-zinc-100 px-4 py-3 font-medium text-zinc-900 transition hover:bg-white"
            >
              Começar a gravar
            </button>
            <p className="text-xs text-zinc-500">
              O texto aparece enquanto você fala. O áudio fica guardado aqui até subir, então
              perder a conexão no meio não perde a gravação.
            </p>
            {erroInicio ? <p className="text-sm text-red-300">{erroInicio}</p> : null}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3">
                {estado.gravando ? (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                ) : null}
                <span
                  className={
                    'relative inline-flex h-3 w-3 rounded-full ' +
                    (estado.gravando ? 'bg-red-500' : 'bg-zinc-600')
                  }
                />
              </span>
              <span className="font-mono text-2xl tabular-nums text-zinc-100">
                {duracaoLegivel(estado.duracaoMs)}
              </span>
              <span className="text-xs text-zinc-500">
                {estado.gravando ? 'gravando' : 'encerrando…'}
              </span>
            </div>

            <div className="h-2 overflow-hidden rounded-full bg-zinc-800">
              <div
                className="h-full bg-emerald-500 transition-[width] duration-75"
                style={{ width: nivel + '%' }}
              />
            </div>

            <p className="text-xs text-zinc-500">
              {estado.pendentes > 0
                ? `${estado.pendentes} pedaço(s) esperando para subir`
                : 'tudo enviado'}
            </p>
            {estado.erro ? <p className="text-sm text-amber-300">{estado.erro}</p> : null}

            <button
              onClick={() => void encerrar()}
              disabled={!estado.gravando}
              className="w-full rounded-md border border-zinc-700 px-4 py-3 font-medium text-zinc-200 transition hover:bg-zinc-800 disabled:opacity-50"
            >
              Parar e transcrever
            </button>
          </div>
        )}
      </Cartao>
    </Tela>
  );
}

export const rotaGravar = createRoute({
  getParentRoute: () => rotaPainel,
  path: '/audio/gravar',
  component: Gravar,
});
